import PersonIcon from '@mui/icons-material/Person';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { updateUser } from 'lib/api';
import { assignRoleToUser } from 'lib/api/users/roles';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import {
  rutChanger,
  showAlert,
  validaRut,
  validateChileanPhone,
} from 'lib/functions/utils';
import { Role } from 'lib/types/models';
import React from 'react';
import { useDispatch } from 'react-redux';

import { UserInterface } from '../..';
import { nullUserState } from '../..';

interface EditUserDialogProps {
  handleCloseDialog: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  openEditDialog: boolean;
  editType: 'password' | 'info';
  fetchUsers: () => Promise<void>;
  roles: Role[];
  setRoles: React.Dispatch<React.SetStateAction<Role[]>>;
  selectedUser: UserInterface;
  setSelectedUser: React.Dispatch<React.SetStateAction<UserInterface>>;
  hasDigitalSignature: boolean;
}

const EditUserDialog = (props: EditUserDialogProps) => {
  const {
    handleCloseDialog,
    loading,
    setLoading,
    openEditDialog,
    editType,
    fetchUsers,
    roles,
    selectedUser,
    setSelectedUser,
    hasDigitalSignature,
  } = props;
  const dispatch = useDispatch();
  const identifierHasError =
    hasDigitalSignature &&
    (!selectedUser?.identifier?.length || !validaRut(selectedUser.identifier));
  const phoneHasError =
    hasDigitalSignature &&
    (!selectedUser?.phone?.length || !validateChileanPhone(selectedUser.phone));
  const identifierErrorMessage = !selectedUser?.identifier?.length
    ? 'Campo obligatorio'
    : 'Formato inválido (12345678-K)';
  const phoneErrorMessage = !selectedUser?.phone?.length
    ? 'Campo obligatorio'
    : 'Teléfono inválido (569XXXXXXXX)';

  const handleSaveEditDialog = async () => {
    try {
      if (
        hasDigitalSignature &&
        (!selectedUser?.identifier?.trim() || !selectedUser?.phone?.trim())
      ) {
        showAlert(dispatch, 'error', 'El RUT y el teléfono son obligatorios.');
        return;
      }
      setLoading(true);
      const payload = {
        id: selectedUser.id,
        name: selectedUser.name,
        identifier: selectedUser.identifier,
        phone: selectedUser.phone,
        isSigner: selectedUser.isSigner,
        ...(selectedUser.password?.length && {
          password: selectedUser.password,
        }),
      };
      await updateUser(dispatch, payload);
      setSelectedUser(nullUserState);
      await fetchUsers();
      handleCloseDialog();
      showAlert(dispatch, 'success', 'Se ha editado el usuario.');
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAssignationsEditDialog = async () => {
    try {
      setLoading(true);
      const rolesIds = (selectedUser.rolesNames || [])
        .map(
          (roleName: string) =>
            roles.find((role: Role) => role.name === roleName)?.id,
        )
        .filter((id): id is number => typeof id === 'number');
      const payload = {
        id: selectedUser.id,
        rolesIds,
      };
      await assignRoleToUser(dispatch, payload);
      setSelectedUser(nullUserState);
      await fetchUsers();
      handleCloseDialog();
      showAlert(
        dispatch,
        'success',
        'Se ha editado el usuario. Recuerda que con esto cambian las notificaciones, para revisarlas el usuario puede ir a su perfil.',
      );
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedUser((prev: UserInterface) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string[]>) => {
    const { name, value } = e.target;
    setSelectedUser((prev: UserInterface) => ({
      ...prev,
      [name]: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleRutChange = (value: string) => {
    rutChanger(value, (formatted: string) =>
      setSelectedUser((prev: UserInterface) => ({
        ...prev,
        identifier: formatted,
      })),
    );
  };

  if (loading) return <CenteredCircularProgress />;

  return (
    <Dialog fullWidth open={openEditDialog} onClose={() => handleCloseDialog()}>
      <DialogTitle>
        <Title
          title={`Editar ${selectedUser.email}`}
          icon={<PersonIcon color="primary" />}
          color="primary"
        />
      </DialogTitle>
      {editType === 'password' ? (
        <DialogContent>
          <TextField
            margin="dense"
            name="name"
            label="Nombre"
            fullWidth
            value={selectedUser.name}
            onChange={handleInputChange}
          />
          {hasDigitalSignature && (
            <>
              <TextField
                margin="dense"
                name="identifier"
                label="RUT"
                value={selectedUser.identifier}
                onChange={(e) => handleRutChange(e.target.value)}
                fullWidth
                error={identifierHasError}
                helperText={identifierHasError ? identifierErrorMessage : ''}
              />
              <TextField
                margin="dense"
                name="phone"
                label="Teléfono"
                value={selectedUser.phone}
                onChange={handleInputChange}
                fullWidth
                error={phoneHasError}
                helperText={phoneHasError ? phoneErrorMessage : ''}
              />
            </>
          )}
          <TextField
            margin="dense"
            name="password"
            label="Contraseña"
            value={selectedUser.password}
            onChange={handleInputChange}
            type="password"
            fullWidth
          />
          {hasDigitalSignature && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedUser.isSigner}
                  onChange={(_, checked) =>
                    setSelectedUser((prev: UserInterface) => ({
                      ...prev,
                      isSigner: checked,
                    }))
                  }
                  color="primary"
                />
              }
              label="Asignar como firmante"
            />
          )}
        </DialogContent>
      ) : (
        <DialogContent style={{ paddingTop: 20 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-mutiple-checkbox-label">Permisos</InputLabel>
            <Select
              labelId="demo-mutiple-checkbox-label"
              id="demo-mutiple-checkbox"
              label="Permisos"
              multiple
              value={selectedUser.rolesNames}
              name="rolesNames"
              onChange={handleSelectChange}
              input={<Input />}
              renderValue={(selected) => selected.join(', ')}
            >
              {roles.map((role: Role) => (
                <MenuItem key={role.id} value={role.name}>
                  <Checkbox
                    checked={(selectedUser.rolesNames || []).includes(
                      role.name,
                    )}
                  />
                  <ListItemText primary={role.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
      )}
      <DialogActions>
        <CustomButton
          color="primary"
          onClick={() =>
            editType === 'password'
              ? handleSaveEditDialog()
              : handleSaveAssignationsEditDialog()
          }
        >
          Guardar
        </CustomButton>
        <CustomButton onClick={() => handleCloseDialog()} color="secondary">
          Salir
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
